// One set of languages and one working language for the whole Smart CMS.
// Each section keeps its own storage (Articles' locale list, the Service Map's languages table, each site's published list);
// this hook presents them as one team list and keeps every section working in the same language

import { useCallback, useEffect, useMemo, useState } from "react"
import type { useLocalCMS } from "../articles/useLocalCMS"
import type { useServiceCMS } from "../service-map/useServiceCMS"
import type { useSiteConfigCMS } from "../site-config/useSiteConfigCMS"
import * as svcStore from "../service-map/store"
import * as scStore from "../site-config/store"
import { atLeast, type CmsPermissions } from "./permissions"
import { articlesAdapter, serviceMapAdapter, siteConfigAdapter, SECTION_LABELS, type SectionKey } from "./localeSyncAdapters"
import { coverageOf, describeLanguage, nativeLanguageName, sortLanguages, type Coverage, type LanguageInfo } from "./languages"
import { localeUsage, localeRemovalBlocker } from "./localeUsage"
import { isSiteLocale } from "./siteLanguages"
import type { SectionOverview, SiteSummary } from "./components/languages/LanguagesPanel"

const WORKING_KEY = "kb-agent-working-locale"

export interface RemovalBlocker {
  kind: "source" | "content" | "published" | "default" | "permission"
  message: string
  siteId?: string
  // A safe one-click fix, where there is one
  fix?: { label: string; run: () => void }
}

interface Sections {
  cms: ReturnType<typeof useLocalCMS>
  svc: ReturnType<typeof useServiceCMS>
  siteconfig: ReturnType<typeof useSiteConfigCMS>
  perms: CmsPermissions
}

export function useTeamLanguages({ cms, svc, siteconfig, perms }: Sections) {
  const [working, setWorkingState] = useState<string>(() => {
    try { return localStorage.getItem(WORKING_KEY) || cms.locale || cms.store.defaultLocale } catch { return cms.store.defaultLocale }
  })
  const setWorking = useCallback((code: string) => {
    setWorkingState(code)
    try { localStorage.setItem(WORKING_KEY, code) } catch { /* private window */ }
  }, [])

  // Every section works in the same language. Their hooks restore a saved locale when a team loads, so this re-applies it
  useEffect(() => { if (cms.locale !== working) cms.setLocale(working) }, [working, cms.locale]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (svc.locale !== working) svc.setLocale(working) }, [working, svc.locale]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (siteconfig.locale !== working) siteconfig.setLocale(working) }, [working, siteconfig.locale]) // eslint-disable-line react-hooks/exhaustive-deps

  const sites = useMemo<SiteSummary[]>(() => Object.values(siteconfig.store.sites).map((s: any) => ({
    id: s.id, name: s.name || "Untitled site", locales: s.locales ?? [], defaultLocale: s.defaultLocale,
  })), [siteconfig.store.sites])

  const canEdit = (section: SectionKey) => atLeast(perms[section], "edit")
  const canView = (section: SectionKey) => atLeast(perms[section], "view")

  const sections = useMemo<SectionOverview[]>(() => ([
    {
      section: "articles", label: SECTION_LABELS.articles, sourceLocale: cms.store.defaultLocale,
      listed: cms.store.locales, hasRegistry: true, canEdit: canEdit("articles"),
      sources: articlesAdapter.sources(cms.store),
    },
    {
      section: "serviceMap", label: SECTION_LABELS.serviceMap, sourceLocale: svc.store.defaultLocale,
      listed: Object.keys(svc.store.languages), hasRegistry: true, canEdit: canEdit("serviceMap"),
      sources: serviceMapAdapter.sources(svc.store),
    },
    {
      section: "siteConfig", label: SECTION_LABELS.siteConfig, sourceLocale: siteconfig.store.defaultLocale,
      listed: siteConfigAdapter.locales(siteconfig.store), hasRegistry: false, canEdit: canEdit("siteConfig"),
      sources: siteConfigAdapter.sources(siteconfig.store),
    },
  ] as SectionOverview[]).filter(s => canView(s.section)), [cms.store, svc.store, siteconfig.store, perms]) // eslint-disable-line react-hooks/exhaustive-deps

  // The team's languages: everything any section lists or has text in, plus the working one
  const languages = useMemo<LanguageInfo[]>(() => {
    const codes = new Set<string>([working])
    for (const s of sections) { codes.add(s.sourceLocale); s.listed.forEach(c => codes.add(c)) }
    for (const site of sites) site.locales.forEach(c => codes.add(c))
    codes.delete("")
    return sortLanguages(
      [...codes].map(c => describeLanguage(c, svc.store.languages[c] as any)),
      cms.store.defaultLocale,
    )
  }, [sections, sites, working, svc.store.languages, cms.store.defaultLocale])

  const infoOf = useCallback((code: string) => languages.find(l => l.code === code) ?? describeLanguage(code), [languages])

  // Coverage per section and language, for the page switcher and browser footers
  const coverage = useMemo(() => {
    const out = new Map<string, Coverage>()
    for (const s of sections) {
      const records = s.sources.map(x => x.record)
      for (const l of languages) out.set(`${s.section}:${l.code}`, coverageOf(records, l.code, s.sourceLocale))
    }
    return out
  }, [sections, languages])
  const coverageFor = useCallback((section: SectionKey, code: string) => coverage.get(`${section}:${code}`), [coverage])

  const addToSection = useCallback((section: SectionKey, code: string) => {
    if (!isSiteLocale(code)) return
    const info = describeLanguage(code)
    if (section === "articles" && canEdit("articles")) cms.addLocale(code)
    // Saved in the language itself: the public site shows this name to readers
    if (section === "serviceMap" && canEdit("serviceMap")) svc.addLocale(code, nativeLanguageName(code), info.direction)
  }, [cms.addLocale, svc.addLocale, perms]) // eslint-disable-line react-hooks/exhaustive-deps

  // A new language goes into every section the role can edit, and becomes the working one
  const addLanguage = useCallback((code: string) => {
    if (!isSiteLocale(code)) return
    addToSection("articles", code)
    addToSection("serviceMap", code)
    setWorking(code)
  }, [addToSection, setWorking])

  // Everything in the way of removing a language. Unpublishing from a site comes with its fix; text has to be dealt with by hand
  const removalBlockers = useCallback((code: string): RemovalBlocker[] => {
    const name = infoOf(code).name
    const out: RemovalBlocker[] = []
    for (const s of sections) {
      if (s.sourceLocale === code) out.push({ kind: "source", message: `It's the source language of ${s.label}.` })
    }
    if (out.length) return out

    if (cms.store.locales.includes(code)) {
      const b = cms.localeRemovalError(code)
      if (b) out.push({ kind: "content", message: `Articles: ${b}` })
      else if (!canEdit("articles")) out.push({ kind: "permission", message: "Your role can't change the Articles languages." })
    }
    if (svc.store.languages[code]) {
      const b = svcStore.localeRemovalError(svc.store, code)
      if (b) out.push({ kind: "content", message: `Service Map: ${b}` })
      else if (!canEdit("serviceMap")) out.push({ kind: "permission", message: "Your role can't change the Service Map languages." })
    }
    const pages = localeRemovalBlocker(localeUsage(
      Object.values(siteconfig.store.pageContents) as any[], code,
      (pc: any) => scStore.displayName(siteconfig.store, "pageContent", pc.id, siteconfig.store.defaultLocale),
      "",
    ), code)
    if (pages) out.push({ kind: "content", message: `Site Config: ${pages}` })
    for (const site of sites) {
      if (scStore.siteHasLocale(siteconfig.store.sites[site.id], code)) {
        out.push({ kind: "content", message: `${site.name} still has ${name} header, footer or cookie banner text.` })
      }
      if (site.defaultLocale === code) {
        out.push({ kind: "default", siteId: site.id, message: `It's the default language of ${site.name}. Pick another default in that site's settings.` })
      } else if (site.locales.includes(code)) {
        out.push({
          kind: "published", siteId: site.id,
          message: `${site.name} offers it.`,
          fix: canEdit("siteConfig") ? { label: `Stop offering on ${site.name}`, run: () => unpublish(site.id, code) } : undefined,
        })
      }
    }
    return out
  }, [sections, cms, svc.store, siteconfig.store, sites, infoOf, perms]) // eslint-disable-line react-hooks/exhaustive-deps

  // Takes a language off one site's published list
  const unpublish = useCallback((siteId: string, code: string) => {
    const site: any = siteconfig.store.sites[siteId]
    if (!site) return
    siteconfig.update("site", siteId, { locales: (site.locales ?? []).filter((c: string) => c !== code) })
  }, [siteconfig])

  // One line for tooltips and the assistant
  const removalBlocker = useCallback((code: string): string | null => {
    const b = removalBlockers(code)
    return b.length ? b.map(x => x.message).join(" ") : null
  }, [removalBlockers])

  const removeLanguage = useCallback((code: string) => {
    if (removalBlockers(code).length) return
    if (cms.store.locales.includes(code)) cms.removeLocale(code)
    if (svc.store.languages[code]) {
      try { svc.remove("language", code) } catch (e) { console.warn("[languages] couldn't remove from the Service Map:", e) }
    }
    if (working === code) setWorking(cms.store.defaultLocale)
  }, [removalBlockers, cms, svc, working, setWorking])

  return {
    working, setWorking, languages, infoOf, sections, sites,
    coverageFor, addLanguage, addToSection, removalBlockers, removalBlocker, removeLanguage,
  }
}
